import { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { 
  Presentation, 
  Upload, 
  FileText, 
  ArrowRight, 
  AlertCircle, 
  ArrowLeft, 
  TrendingUp,
  CheckCircle,
  Loader2,
  ChevronLeft,
  ChevronRight,
  Play,
  RefreshCw
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function UserCreateSession() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [pageLoading, setPageLoading] = useState(true);
  
  // Form state
  const [formData, setFormData] = useState({
    topic_name: "",
    concept_overview: "",
    notes: ""
  });
  const [presentationFile, setPresentationFile] = useState(null);
  
  // Process state
  const [step, setStep] = useState(1); // 1: Form, 2: Processing, 3: Preview
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [slides, setSlides] = useState([]);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [extractionStatus, setExtractionStatus] = useState('idle');

  useEffect(() => {
    fetchUser();
  }, [token]);

  const fetchUser = async () => {
    try {
      const response = await axios.get(`${API}/users/token/${token}`);
      setUser(response.data);
    } catch (error) {
      toast.error("Invalid link");
      navigate("/");
    } finally {
      setPageLoading(false);
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const validTypes = ['.ppt', '.pptx', '.pdf', '.key', '.odp', '.odf', '.pptm'];
      const fileExt = '.' + file.name.split('.').pop().toLowerCase();
      if (!validTypes.includes(fileExt)) {
        toast.error("Please upload a valid presentation file (PPT, PPTX, PDF, or KEY)");
        return;
      }
      setPresentationFile(file);
      setSlides([]);
      setExtractionStatus('idle');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.topic_name) {
      toast.error("Please enter a topic name");
      return;
    }

    if (!presentationFile) {
      toast.error("Please upload a presentation file");
      return;
    }

    setStep(2);
    setLoading(true);
    setExtractionStatus('uploading');

    try {
      // Create session
      const sessionResponse = await axios.post(`${API}/sessions`, {
        ...formData,
        user_id: user.id
      });
      const newSessionId = sessionResponse.data.id;
      setSessionId(newSessionId);

      // Upload presentation file - returns immediately, processing in background
      toast.info("Uploading presentation...");
      setExtractionStatus('extracting');
      const fileFormData = new FormData();
      fileFormData.append('file', presentationFile);
      
      const uploadResponse = await axios.post(`${API}/sessions/${newSessionId}/upload-presentation`, fileFormData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 60000 // 60 second timeout for upload only
      });

      console.log("Upload response:", uploadResponse.data);
      toast.info("Processing presentation... This may take a moment.");

      // Poll for processing completion (up to 120 seconds)
      let processingComplete = false;
      let pollAttempts = 0;
      const maxAttempts = 60; // 60 attempts * 2 seconds = 120 seconds max wait
      let stuckInProcessing = 0; // Track how long we've been in "processing" state
      
      while (!processingComplete && pollAttempts < maxAttempts) {
        await new Promise(resolve => setTimeout(resolve, 2000)); // Wait 2 seconds between polls
        
        try {
          // Check processing status first
          const statusResponse = await axios.get(`${API}/sessions/${newSessionId}/processing-status`);
          console.log(`Processing status (attempt ${pollAttempts + 1}):`, statusResponse.data);
          
          const { status, slide_count, has_pdf, error } = statusResponse.data;
          
          // Check if we're stuck in "processing" state
          if (status === 'processing') {
            stuckInProcessing++;
            if (stuckInProcessing >= 10) { // After 20 seconds of no progress
              console.log("Stuck in processing state, triggering manual retry...");
              toast.info("Processing seems stuck, retrying...");
              try {
                await axios.post(`${API}/sessions/${newSessionId}/retry-processing`);
                stuckInProcessing = 0; // Reset counter after retry
              } catch (retryErr) {
                console.log("Retry failed:", retryErr);
              }
            }
          } else {
            stuckInProcessing = 0;
          }
          
          if (status === 'failed') {
            // Provide helpful error message
            let errorMsg = error || 'Unknown error';
            if (errorMsg.includes('No conversion service configured')) {
              errorMsg = 'PowerPoint conversion not configured. Please contact your administrator.';
            }
            toast.error(errorMsg, { duration: 10000 });
            setExtractionStatus('error');
            setStep(3); // Move to step 3 so user can see error state
            setLoading(false);
            processingComplete = true;
            break;
          }
          
          // Check for slides
          const slidesResponse = await axios.get(`${API}/sessions/${newSessionId}/slides`);
          console.log(`Slides check (attempt ${pollAttempts + 1}):`, slidesResponse.data);
          
          if (slidesResponse.data.slides && slidesResponse.data.slides.length > 0) {
            setSlides(slidesResponse.data.slides);
            processingComplete = true;
            setExtractionStatus('done');
            setStep(3);
            setLoading(false);
            toast.success(`Presentation ready! ${slidesResponse.data.total} slides extracted.`);
            break;
          } else if (slidesResponse.data.file_type === 'pdf' || slidesResponse.data.pdf_url || has_pdf) {
            // PDF is available but no image slides - acceptable
            processingComplete = true;
            setExtractionStatus('done');
            setStep(3);
            setLoading(false);
            toast.success("Presentation uploaded! Slide preview ready.");
            break;
          }
          
          // Update status message
          if (status === 'converting') {
            setExtractionStatus('converting');
          } else if (status === 'extracting') {
            setExtractionStatus('extracting');
          }
          
        } catch (pollError) {
          console.log(`Poll attempt ${pollAttempts + 1} failed:`, pollError);
        }
        
        pollAttempts++;
      }
      
      // If still not ready after polling, try manual reconversion
      if (!processingComplete) {
        toast.warning("Slide extraction is taking longer than expected. Attempting retry...");
        
        try {
          await axios.post(`${API}/sessions/${newSessionId}/reconvert-presentation`);
          await new Promise(resolve => setTimeout(resolve, 5000));
          
          const finalResponse = await axios.get(`${API}/sessions/${newSessionId}/slides`);
          
          if (finalResponse.data.slides && finalResponse.data.slides.length > 0) {
            setSlides(finalResponse.data.slides);
            setExtractionStatus('done');
            setStep(3);
            setLoading(false);
            toast.success(`Slides ready! ${finalResponse.data.total} slides extracted.`);
          } else {
            setExtractionStatus('done');
            setStep(3);
            setLoading(false);
            toast.warning("Slide preview may be limited. You can still start your session.");
          }
        } catch (retryError) {
          console.error("Retry conversion failed:", retryError);
          setExtractionStatus('failed');
          setStep(3);
          setLoading(false);
          toast.error("Slide extraction failed. Please try uploading a different file format.");
        }
      }

    } catch (error) {
      console.error("Session creation failed:", error);
      setExtractionStatus('failed');
      toast.error(error.response?.data?.detail || "Failed to process presentation");
      setLoading(false);
    }
  };

  const retryExtraction = async () => {
    if (!sessionId) return;
    setExtractionStatus('extracting');
    setLoading(true);
    
    try {
      // Try reconversion
      await axios.post(`${API}/sessions/${sessionId}/reconvert-presentation`);
      await new Promise(resolve => setTimeout(resolve, 5000));
      
      const slidesResponse = await axios.get(`${API}/sessions/${sessionId}/slides`);
      
      if (slidesResponse.data.slides && slidesResponse.data.slides.length > 0) {
        setSlides(slidesResponse.data.slides);
        setExtractionStatus('done');
        setStep(3);
        toast.success(`Slides ready! ${slidesResponse.data.total} slides extracted.`);
      } else {
        setExtractionStatus('done');
        setStep(3);
        toast.warning("Slide preview not available. You can still start your session.");
      }
    } catch (error) {
      setExtractionStatus('failed');
      toast.error("Failed to extract slides");
    } finally {
      setLoading(false);
    }
  };

  const startSession = () => {
    if (sessionId) {
      navigate(`/session/${sessionId}/live`);
    }
  };

  const nextSlide = () => {
    if (currentSlide < slides.length - 1) {
      setCurrentSlide(currentSlide + 1);
    }
  };

  const prevSlide = () => {
    if (currentSlide > 0) {
      setCurrentSlide(currentSlide - 1);
    }
  };

  if (pageLoading) {
    return (
      <div className="min-h-screen bg-[#F9F8F6] flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  // Header component
  const Header = () => (
    <header className="bg-white border-b border-border">
      <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-primary flex items-center justify-center">
            <TrendingUp className="w-5 h-5 text-white" />
          </div>
          <span className="text-xl font-bold text-primary" style={{ fontFamily: 'Manrope, sans-serif' }}>
            EVOLVE
          </span>
        </div>
        <Link to={`/u/${token}`}>
          <Button variant="ghost" className="rounded-full">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Dashboard
          </Button>
        </Link>
      </div>
    </header>
  );

  // Progress bar component
  const ProgressBar = () => (
    <div className="flex items-center gap-2">
      <div className="flex items-center gap-2">
        <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
          step > 1 ? 'bg-[#059669] text-white' : 'bg-primary text-white'
        }`}>
          {step > 1 ? <CheckCircle className="w-4 h-4" /> : '1'}
        </div>
        <span className={`text-sm ${step >= 1 ? 'text-primary font-medium' : 'text-[#5C5C5C]'}`}>Details</span>
      </div>
      <div className={`flex-1 h-1 rounded ${step > 1 ? 'bg-[#059669]' : 'bg-border'}`} />
      <div className="flex items-center gap-2">
        <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
          step > 2 ? 'bg-[#059669] text-white' : step === 2 ? 'bg-primary text-white' : 'bg-border text-[#5C5C5C]'
        }`}>
          {step > 2 ? <CheckCircle className="w-4 h-4" /> : '2'}
        </div>
        <span className={`text-sm ${step >= 2 ? 'text-primary font-medium' : 'text-[#5C5C5C]'}`}>Process</span>
      </div>
      <div className={`flex-1 h-1 rounded ${step > 2 ? 'bg-[#059669]' : 'bg-border'}`} />
      <div className="flex items-center gap-2">
        <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
          step === 3 ? 'bg-primary text-white' : 'bg-border text-[#5C5C5C]'
        }`}>3</div>
        <span className={`text-sm ${step === 3 ? 'text-primary font-medium' : 'text-[#5C5C5C]'}`}>Preview</span>
      </div>
    </div>
  );

  // Step 1: Form
  if (step === 1) {
    return (
      <div className="min-h-screen bg-[#F9F8F6]">
        <Header />
        <main className="max-w-2xl mx-auto px-6 py-8 space-y-6">
          <div>
            <h1 className="text-3xl font-bold text-[#1A1A1A] tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Create EVOLVE Session
            </h1>
            <p className="text-[#5C5C5C] mt-1">
              Hi {user?.name}, let&apos;s set up your presentation
            </p>
          </div>

          <ProgressBar />

          <Card className="stats-card">
            <CardHeader>
              <CardTitle className="text-lg font-semibold flex items-center gap-2">
                <Presentation className="w-5 h-5 text-primary" />
                Session Details
              </CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <Label htmlFor="topic_name">Presentation Topic *</Label>
                  <Input
                    id="topic_name"
                    data-testid="topic-input"
                    placeholder="e.g., Q4 Sales Strategy Review"
                    value={formData.topic_name}
                    onChange={(e) => setFormData({ ...formData, topic_name: e.target.value })}
                    required
                  />
                </div>

                <div>
                  <Label htmlFor="concept_overview">Concept Overview *</Label>
                  <Textarea
                    id="concept_overview"
                    data-testid="overview-input"
                    placeholder="Brief description of what you'll be presenting..."
                    value={formData.concept_overview}
                    onChange={(e) => setFormData({ ...formData, concept_overview: e.target.value })}
                    rows={3}
                    required
                  />
                </div>

                <div>
                  <Label htmlFor="notes">Additional Notes (Optional)</Label>
                  <Textarea
                    id="notes"
                    data-testid="notes-input"
                    placeholder="Any specific areas you want feedback on..."
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    rows={2}
                  />
                </div>

                <div>
                  <Label className="flex items-center gap-2">
                    Presentation File *
                    <span className="text-xs text-accent font-normal">(Required)</span>
                  </Label>
                  <div 
                    className={`mt-2 border-2 border-dashed rounded-xl p-8 text-center transition-colors cursor-pointer ${
                      presentationFile ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/50'
                    }`}
                    onClick={() => document.getElementById('file-upload').click()}
                  >
                    <input
                      id="file-upload"
                      type="file"
                      className="hidden"
                      accept=".ppt,.pptx,.pdf,.key,.odp,.odf,.pptm"
                      onChange={handleFileChange}
                      data-testid="file-upload"
                    />
                    {presentationFile ? (
                      <div className="flex items-center justify-center gap-3">
                        <FileText className="w-8 h-8 text-primary" />
                        <div className="text-left">
                          <p className="font-medium text-[#1A1A1A]">{presentationFile.name}</p>
                          <p className="text-sm text-[#5C5C5C]">
                            {(presentationFile.size / 1024 / 1024).toFixed(2)} MB
                          </p>
                        </div>
                        <CheckCircle className="w-5 h-5 text-[#059669]" />
                      </div>
                    ) : (
                      <>
                        <Upload className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
                        <p className="text-[#5C5C5C]">Click to upload your presentation</p>
                        <p className="text-sm text-[#9CA3AF] mt-1">PPT, PPTX, PDF files supported</p>
                      </>
                    )}
                  </div>
                  
                  {!presentationFile && (
                    <div className="flex items-center gap-2 mt-2 p-3 bg-[#FEF3C7] rounded-lg">
                      <AlertCircle className="w-4 h-4 text-[#D97706]" />
                      <p className="text-sm text-[#92400E]">
                        A presentation file is required to continue
                      </p>
                    </div>
                  )}
                </div>

                <div className="flex items-center justify-end pt-4 border-t border-border">
                  <Button
                    type="submit"
                    data-testid="upload-and-process-btn"
                    disabled={!presentationFile || !formData.topic_name}
                    className="bg-primary hover:bg-primary/90 rounded-full px-8"
                  >
                    Upload & Process
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </main>
      </div>
    );
  }

  // Step 2: Processing
  if (step === 2) {
    return (
      <div className="min-h-screen bg-[#F9F8F6]">
        <Header />
        <main className="max-w-2xl mx-auto px-6 py-8 space-y-6">
          <div>
            <h1 className="text-3xl font-bold text-[#1A1A1A] tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Processing Presentation
            </h1>
            <p className="text-[#5C5C5C] mt-1">
              Extracting slides from your presentation
            </p>
          </div>

          <ProgressBar />

          <Card className="stats-card">
            <CardContent className="py-16">
              <div className="text-center space-y-6">
                {extractionStatus === 'uploading' && (
                  <>
                    <Loader2 className="w-16 h-16 text-primary animate-spin mx-auto" />
                    <div>
                      <h2 className="text-xl font-semibold text-[#1A1A1A]">Uploading Presentation</h2>
                      <p className="text-[#5C5C5C] mt-1">Please wait while we upload your file...</p>
                    </div>
                  </>
                )}
                
                {extractionStatus === 'extracting' && (
                  <>
                    <Loader2 className="w-16 h-16 text-primary animate-spin mx-auto" />
                    <div>
                      <h2 className="text-xl font-semibold text-[#1A1A1A]">Extracting Slides</h2>
                      <p className="text-[#5C5C5C] mt-1">Converting your presentation to slides...</p>
                      <p className="text-sm text-[#9CA3AF] mt-2">This may take a moment</p>
                    </div>
                  </>
                )}

                {extractionStatus === 'failed' && (
                  <>
                    <AlertCircle className="w-16 h-16 text-[#DC2626] mx-auto" />
                    <div>
                      <h2 className="text-xl font-semibold text-[#1A1A1A]">Processing Failed</h2>
                      <p className="text-[#5C5C5C] mt-1">There was an error processing your presentation</p>
                    </div>
                    <div className="flex justify-center gap-4">
                      <Button variant="outline" onClick={() => setStep(1)} className="rounded-full">
                        Go Back
                      </Button>
                      <Button onClick={retryExtraction} className="bg-primary hover:bg-primary/90 rounded-full">
                        <RefreshCw className="w-4 h-4 mr-2" />
                        Retry
                      </Button>
                    </div>
                  </>
                )}

                <div className="flex items-center justify-center gap-3 p-4 bg-secondary/50 rounded-xl max-w-sm mx-auto">
                  <FileText className="w-8 h-8 text-primary" />
                  <div className="text-left">
                    <p className="font-medium text-[#1A1A1A] truncate max-w-[200px]">{presentationFile?.name}</p>
                    <p className="text-sm text-[#5C5C5C]">
                      {presentationFile && (presentationFile.size / 1024 / 1024).toFixed(2)} MB
                    </p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </main>
      </div>
    );
  }

  // Step 3: Preview
  return (
    <div className="min-h-screen bg-[#F9F8F6]">
      <Header />
      <main className="max-w-4xl mx-auto px-6 py-8 space-y-6">
        <div>
          <h1 className="text-3xl font-bold text-[#1A1A1A] tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Preview Your Presentation
          </h1>
          <p className="text-[#5C5C5C] mt-1">
            Review your slides and start when ready
          </p>
        </div>

        <ProgressBar />

        {/* Session Info */}
        <Card className="stats-card">
          <CardContent className="py-4">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="font-semibold text-[#1A1A1A]">{formData.topic_name}</h2>
                <p className="text-sm text-[#5C5C5C]">{formData.concept_overview}</p>
              </div>
              <div className="flex items-center gap-2 text-sm text-[#059669]">
                <CheckCircle className="w-4 h-4" />
                Ready to present
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Slide Preview */}
        <Card className="stats-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg font-semibold">
              Slide Preview {slides.length > 0 && `(${slides.length} slides)`}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {slides.length > 0 ? (
              <div className="space-y-4">
                <div className="aspect-video bg-white rounded-xl border-2 border-border overflow-hidden relative">
                  <img 
                    src={`${process.env.REACT_APP_BACKEND_URL}${slides[currentSlide]}`}
                    alt={`Slide ${currentSlide + 1}`}
                    className="w-full h-full object-contain"
                  />
                  <div className="absolute bottom-4 right-4 px-3 py-1 bg-primary/90 rounded-full text-sm text-white font-medium">
                    {currentSlide + 1} / {slides.length}
                  </div>
                </div>

                <div className="flex items-center justify-center gap-4">
                  <Button
                    variant="outline"
                    onClick={prevSlide}
                    disabled={currentSlide === 0}
                    className="rounded-full"
                  >
                    <ChevronLeft className="w-5 h-5" />
                    Previous
                  </Button>
                  <Button
                    variant="outline"
                    onClick={nextSlide}
                    disabled={currentSlide === slides.length - 1}
                    className="rounded-full"
                  >
                    Next
                    <ChevronRight className="w-5 h-5" />
                  </Button>
                </div>

                <div className="flex gap-2 justify-center overflow-x-auto pb-2">
                  {slides.map((slide, idx) => (
                    <button
                      key={idx}
                      onClick={() => setCurrentSlide(idx)}
                      className={`flex-shrink-0 w-20 h-12 rounded-lg overflow-hidden border-2 transition-all ${
                        idx === currentSlide ? 'border-primary ring-2 ring-primary/20' : 'border-border opacity-60 hover:opacity-100'
                      }`}
                    >
                      <img 
                        src={`${process.env.REACT_APP_BACKEND_URL}${slide}`}
                        alt={`Thumbnail ${idx + 1}`}
                        className="w-full h-full object-cover"
                      />
                    </button>
                  ))}
                </div>
              </div>
            ) : (
              <div className="py-12 text-center">
                <AlertCircle className="w-12 h-12 text-[#D97706] mx-auto mb-4" />
                <h3 className="font-semibold text-[#1A1A1A]">Slide preview not available</h3>
                <p className="text-sm text-[#5C5C5C] mt-1">
                  You can still start your session. Slides will be displayed during the presentation.
                </p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Action buttons */}
        <div className="flex items-center justify-between">
          <Button 
            variant="outline" 
            onClick={() => {
              setStep(1);
              setSessionId(null);
              setSlides([]);
            }}
            className="rounded-full"
          >
            <ChevronLeft className="w-4 h-4 mr-2" />
            Start Over
          </Button>
          <Button
            onClick={startSession}
            data-testid="start-session-btn"
            className="bg-[#059669] hover:bg-[#059669]/90 rounded-full px-8 py-6 text-lg"
          >
            <Play className="w-5 h-5 mr-2" />
            Start Presentation
          </Button>
        </div>
      </main>
    </div>
  );
}
